// src/pages/accounting/ExpenseAdminPage.jsx
// Unified Expense Administration page for admins.
// Consolidates project expenses, office expenses, approval queue, and payouts
// into a single tabbed interface. Delegates rendering to existing page components
// to avoid duplicating their ~3k+ lines of UI/logic.

import React, { useState, useCallback, useMemo } from 'react';
import { BrioFrame, useFullScreenPage, BrioFullScreenButton } from '../../components/BrioComponents';
import ProjectExpensesPage from './ProjectExpensesPage';
import OfficeExpensesPage from './OfficeExpensesPage';
import ReimbursementsPage from './ReimbursementsPage';
import {
  canViewProjectExpenses, canViewOfficeExpenses,
  canViewApprovals, canViewPayouts,
} from '../../utils/expensePermissions';

const ALL_TABS = [
  { id: 'project', label: 'Project Expenses', perm: canViewProjectExpenses },
  { id: 'office', label: 'Office Expenses', perm: canViewOfficeExpenses },
  { id: 'approvals', label: 'Approval Queue', perm: canViewApprovals },
  { id: 'payouts', label: 'Payouts & Vouchers', perm: canViewPayouts },
];

export default function ExpenseAdminPage({
  currentUser,
  isAdmin,
  activeUsers,
  activeProjects,
  getSortedProjects,
  getSupervisedProjects,
  fmtProject,
  activeExpenses,
  projectExpenses,
  setProjectExpenses,
  activeOfficeOverheads,
  officeOverheads,
  setOfficeOverheads,
  form,
  setForm,
  useSupabaseDB,
  supabaseExpenses,
  supabaseOfficeOverheads,
  reimbursementsHook,
  invoicesHook,
  fmtCurr,
  currSymbol,
  onNavigate,
}) {
  // Filter tabs by role permissions
  const TABS = useMemo(
    () => ALL_TABS.filter(t => t.perm(currentUser)),
    [currentUser]
  );
  const [activeTab, setActiveTab] = useState(() => TABS[0]?.id || 'project');
  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);

  // For supervisors, filter projects to only those they supervise
  const filteredProjects = useMemo(() => {
    if (isAdmin) return activeProjects;
    if (getSupervisedProjects) return getSupervisedProjects();
    return activeProjects;
  }, [isAdmin, activeProjects, getSupervisedProjects]);

  // Filter expenses to only those belonging to supervised projects
  const filteredExpenses = useMemo(() => {
    if (isAdmin) return activeExpenses;
    const projIds = new Set(filteredProjects.map(p => p.id));
    return (activeExpenses || []).filter(e => projIds.has(e.projectId));
  }, [isAdmin, activeExpenses, filteredProjects]);

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  return (
    <div className={`brio-flex brio-flex-col ${rootClassName}`} style={{ background: '#fff', ...rootStyle }}>
      {/* Tab bar */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-flex brio-items-center brio-gap-xs" style={{ paddingTop: 4, paddingBottom: 0 }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`brio-tab-btn${activeTab === tab.id ? ' brio-tab-btn--active' : ''}`}
                style={{
                  padding: '6px 14px',
                  fontSize: '11px',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text-secondary, #666)',
                  borderBottom: activeTab === tab.id ? '2px solid var(--brio-gold, #9F902A)' : '2px solid transparent',
                  background: 'transparent',
                  border: 'none',
                  borderBottomWidth: 2,
                  borderBottomStyle: 'solid',
                  borderBottomColor: activeTab === tab.id ? 'var(--brio-gold, #9F902A)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  fontFamily: 'inherit',
                  letterSpacing: '0.01em',
                }}
              >
                {tab.label}
              </button>
            ))}
            <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} style={{ marginLeft: 'auto' }} />
          </div>
        </BrioFrame>
      </div>

      {/* Tab content */}
      {activeTab === 'project' && (
        <ProjectExpensesPage
          activeProjects={filteredProjects}
          activeExpenses={filteredExpenses}
          projectExpenses={projectExpenses}
          setProjectExpenses={setProjectExpenses}
          form={form}
          setForm={setForm}
          useSupabaseDB={useSupabaseDB}
          supabaseExpenses={supabaseExpenses}
          supabaseOfficeOverheads={supabaseOfficeOverheads}
          fmtCurr={fmtCurr}
          fmtProject={fmtProject}
          currSymbol={currSymbol}
          getSortedProjects={isAdmin ? getSortedProjects : () => filteredProjects}
          reimbursementsHook={reimbursementsHook}
          invoicesHook={invoicesHook}
        />
      )}

      {activeTab === 'office' && (
        <OfficeExpensesPage
          activeOfficeOverheads={activeOfficeOverheads}
          officeOverheads={officeOverheads}
          setOfficeOverheads={setOfficeOverheads}
          form={form}
          setForm={setForm}
          useSupabaseDB={useSupabaseDB}
          supabaseOfficeOverheads={supabaseOfficeOverheads}
          supabaseExpenses={supabaseExpenses}
          fmtCurr={fmtCurr}
          currSymbol={currSymbol}
        />
      )}

      {activeTab === 'approvals' && (
        <ReimbursementsPage
          currentUser={currentUser}
          isAdmin={isAdmin}
          activeUsers={activeUsers}
          activeProjects={activeProjects}
          getSortedProjects={getSortedProjects}
          fmtProject={fmtProject}
          reimbursementsHook={reimbursementsHook}
          invoicesHook={invoicesHook}
          supabaseExpenses={supabaseExpenses}
          supabaseOfficeOverheads={supabaseOfficeOverheads}
          initialTab="approvals"
        />
      )}

      {activeTab === 'payouts' && (
        <ReimbursementsPage
          currentUser={currentUser}
          isAdmin={isAdmin}
          activeUsers={activeUsers}
          activeProjects={activeProjects}
          getSortedProjects={getSortedProjects}
          fmtProject={fmtProject}
          reimbursementsHook={reimbursementsHook}
          invoicesHook={invoicesHook}
          supabaseExpenses={supabaseExpenses}
          supabaseOfficeOverheads={supabaseOfficeOverheads}
          initialTab="payouts"
        />
      )}
    </div>
  );
}
